/** UI5335: metadata only. Message bodies, media URLs and credentials never enter this store. */
export const SEND_JOURNAL_KEY = "petsoul:send-receipts:v1";
export type SendIdentity = { userId: string | null; petId: string | null; householdId: string | null; homeId: string | null };
export type SendScope = { userId: string; petId: string; householdId: string; homeId: string; householdVersion: number };
export type SendPending = SendScope & { clientMessageId: string; status: "pending" };
export type SendJournalView = { kind: "clear" } | { kind: "pending"; pending: SendPending } | { kind: "held" };
type Store = Pick<Storage, "getItem" | "setItem">;
type Document = { schema: 1; pending: SendPending[] };
const ids = ["userId", "petId", "householdId", "homeId"] as const;
const keys = [...ids, "householdVersion", "clientMessageId", "status"].sort();
const id = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= 256 && v.trim() === v && !/[\u0000-\u001f]/.test(v);
export const validSendIdentity = (v: SendIdentity): v is { userId: string; petId: string; householdId: string; homeId: string } => ids.every(k => id(v[k]));
export const sameSendIdentity = (a: SendIdentity, b: SendIdentity): boolean => ids.every(k => a[k] === b[k]);
export const sameSendScope = (a: SendScope, b: SendScope): boolean => sameSendIdentity(a, b) && a.householdVersion === b.householdVersion;
export const validSendScope = (v: SendScope): boolean => validSendIdentity(v) && Number.isSafeInteger(v.householdVersion) && v.householdVersion >= 0;
const validPending = (v: unknown): v is SendPending => {
 if (!v || typeof v !== "object" || Array.isArray(v)) return false;
 const q = v as SendPending;
 return Object.keys(v).sort().join("|") === keys.join("|") && validSendScope(q)
  && id(q.clientMessageId) && q.clientMessageId.length >= 8 && q.clientMessageId.length <= 64 && q.status === "pending";
};
function parse(raw: string | null): Document {
 if (raw === null) return { schema: 1, pending: [] };
 if (raw.length > 65536) throw Error("journal unavailable");
 const d = JSON.parse(raw) as Document;
 if (!d || d.schema !== 1 || Object.keys(d).sort().join("|") !== "pending|schema" || !Array.isArray(d.pending) || d.pending.length > 64 || !d.pending.every(validPending)) throw Error("journal unavailable");
 const logical = d.pending.map(p => JSON.stringify([p.userId, p.petId]));
 if (new Set(logical).size !== logical.length) throw Error("journal unavailable");
 return d;
}
export function createSendReceiptJournal(storage: () => Store = () => window.localStorage) {
 const read = () => parse(storage().getItem(SEND_JOURNAL_KEY));
 const write = (d: Document) => {
  const serialized = JSON.stringify(d);
  storage().setItem(SEND_JOURNAL_KEY, serialized);
  // Before any POST, demand exact durable readback, including retained unrelated pending records.
  if (storage().getItem(SEND_JOURNAL_KEY) !== serialized) throw Error("journal not durable");
  read();
 };
 return {
  inspect(identity: SendIdentity): SendJournalView {
   try {
    if (!validSendIdentity(identity)) return { kind: "held" };
    const p = read().pending.find(p => p.userId === identity.userId && p.petId === identity.petId);
    if (!p) return { kind: "clear" };
    return sameSendIdentity(p, identity) ? { kind: "pending", pending: Object.freeze({ ...p }) } : { kind: "held" };
   } catch { return { kind: "held" }; }
  },
  begin(scope: SendScope, clientMessageId: string): SendPending {
   const p: SendPending = { ...scope, clientMessageId, status: "pending" };
   if (!validPending(p)) throw Error("invalid journal scope");
   const d = read();
   if (d.pending.length >= 64 || d.pending.some(p => p.userId === scope.userId && p.petId === scope.petId)) throw Error("original operation unresolved");
   write({ schema: 1, pending: [...d.pending, p] });
   return Object.freeze(p);
  },
  resolve(scope: SendScope, clientMessageId: string): boolean {
   if (!validSendScope(scope)) return false;
   const d = read(), original = d.pending.find(p => p.clientMessageId === clientMessageId && sameSendScope(p, scope));
   if (!original) return false;
   write({ schema: 1, pending: d.pending.filter(p => p !== original) });
   return true;
  },
 };
}
export function originalSendReceipt(value: unknown, clientMessageId: string): boolean {
 if (!value || typeof value !== "object" || Array.isArray(value)) return false;
 const q = value as Record<string, unknown>;
 return q.client_message_id === clientMessageId && id(q.message_id) && q.sender === "owner" && q.channel === "private"
  && typeof q.state === "string" && ["sending", "delivered", "awaiting_reply", "processing", "failed"].includes(q.state);
}
/** All scopes share one document: serialize its read/modify/write across tabs; missing locking fails before POST. */
export async function withSendReceiptLock<T>(identity: SendIdentity, operation: () => Promise<T>): Promise<T> {
 if (!validSendIdentity(identity) || !navigator.locks?.request) throw Error("safe local send locking unavailable");
 return navigator.locks.request("petsoul-send-document:" + SEND_JOURNAL_KEY, { mode: "exclusive" }, operation);
}
